import { AlertInlineMain } from '@inmoment/the-kitchen';
import { CodeBlock } from '../cookbook-ui/custom-components/CodeBlock/CodeBlock';

const supportStatementUrl = 'https://knowledge.inmoment.com/Core_Platform_Information/XI_Platform/XI_Browser_Support_Statement';
const appleMacOsSupportUrls = {
  'Use your keyboard like a mouse': 'https://support.apple.com/guide/mac-help/use-your-keyboard-like-a-mouse-mchlp1399/13.0/mac/13.0',
  'Navigate your Mac using Full Keyboard Access':
    'https://support.apple.com/guide/mac-help/navigate-your-mac-using-full-keyboard-access-mchlc06d1059/13.0/mac/13.0',
};
const appleSafariSupportUrl = 'https://support.apple.com/guide/safari/keyboard-and-other-shortcuts-cpsh003/16.1/mac/13.0';

const ExternalLink = (props: { url: string; title: string }) => (
  <a href={props.url} target="_blank" rel="noreferrer">
    {props.title}
  </a>
);

export const FAQs = [
  {
    title: 'How do I install The Kitchen?',
    content: (
      <>
        <p className="kxi-text-large kxi-text-600">CSS</p>
        <br />
        <p>In order for styles to work, they must be imported separately.</p>
        <CodeBlock
          code={`// ES6+
import '@inmoment/the-kitchen/shared-ui-kitchen.esm.css';

// .css & .scss
@import 'node_modules/@inmoment/the-kitchen/shared-ui-kitchen.esm.css';`}
        />
        <br />
        <p>
          <b>You must be connected to the InMoment network to install.</b>
        </p>
        <br />
        <br />
        <p className="kxi-text-600 kxi-text-normal">.npmrc</p>
        <br />
        <CodeBlock>@inmoment:registry=https://nexus.slc1.dev.inmoment.com/repository/inmoment-npm</CodeBlock>
        <br />
        <br />
        <p className="kxi-text-600 kxi-text-normal">Install</p>
        <br />
        <CodeBlock>npm i @inmoment/the-kitchen</CodeBlock>
        <br />
        <br />
        <AlertInlineMain
          title="Note"
          color="admin"
          subtitle="If you are using React, you are ready to simply import the components and start using them. If you are using Angular, keep reading!"
        ></AlertInlineMain>
        <br />
        <CodeBlock
          code={`// React Import
import { XIIcon } from '@inmoment/the-kitchen'
// Start Cookin'!`}
        />
        <br />
        <br />
        <p id="angular-setup" className="kxi-text-large kxi-text-600">
          Angular Setup
        </p>
        <br />
        <br />
        <p className="kxi-text-normal">If you are not running in the monorepo, you will need to perform these first two steps.</p>
        <br />
        <br />
        <p className="kxi-text-large">1.</p>
        <br />
        <CodeBlock
          code={`// in tsconfig.json
"compilerOptions: {
  // ...
  "resolveJsonModule": true,
  "allowSyntheticDefaultImports": true
}`}
        />
        <br />
        <br />
        <p className="kxi-text-large">2.</p>
        <br />
        <p>Install the following.</p>
        <br />
        <CodeBlock>npm install -D react react-dom @types/react @types/react-dom</CodeBlock>
        <br />
        <br />
        <p className="kxi-text-large kxi-text-600">In your angular application, you can use this as a guide to use the components.</p>
        <br />
        <CodeBlock
          code={`// in app.module.ts (or your module that you're using)
import { FABComponent, SearchBoxComponent } from '@inmoment/the-kitchen';
// ...
schemas: [CUSTOM_ELEMENTS_SCHEMA]
// ...
export class AppModule {
  constructor() {
    FABComponent.define();
    SearchBoxComponent.define();
  }
}
`}
        />
      </>
    ),
  },
  {
    title: 'How do I use Two-Way Binding?',
    content: (
      <>
        <p>
          <b>
            The Kitchen Angular Module (available in <code>v12.5.0</code>) provides support for our kitchen components in Angular.
          </b>
          <br />
          <br />
          This includes: <br /> <br /> <em>&nbsp;&nbsp;Two way binding of: </em>
          <br />
          <b>activeId</b>
          <br />
          &nbsp;&nbsp;Accordion <br /> &nbsp;&nbsp;InnerAccordion <br />
          <b>isVisible</b> <br /> &nbsp;&nbsp;WarningPopup <br /> <b>checked</b> <br />
          &nbsp;&nbsp;Checkbox <br /> &nbsp;&nbsp;ListItem <br /> <b>value</b>
          <br /> &nbsp;&nbsp;List <br />
          &nbsp;&nbsp;SearchBox <br /> &nbsp;&nbsp;EditableText <br /> &nbsp;&nbsp;InputField <br /> <b>side</b> <br /> &nbsp;&nbsp;ToggleText <br />{' '}
          <b>activeTabIndex</b> <br />
          &nbsp;&nbsp;TabBar <br /> <b>activeTabId</b> <br /> &nbsp;&nbsp;TabBar <br />{' '}
          <b>Angular Form binding (ngModel/formControl/formControlName)</b> <br />
          &nbsp;&nbsp;Checkbox <br />
          &nbsp;&nbsp;Radio/SVGRadio <em>(requires that name property is set to work correctly)</em>
          <br /> &nbsp;&nbsp;InputField <br />
          &nbsp;&nbsp;Dropdown <em>(this only binds the value outbound, initial value is not applied [functionality coming soon])</em> <br />{' '}
          &nbsp;&nbsp;List <br />
          &nbsp;&nbsp;LeftRightPicker <em>(this only binds the value outbound, initial value is not applied [functionality coming soon])</em> <br />
          <br />
          <p className="kxi-text-large kxi-text-600">Install Instructions for Two-Way Binding </p>
          <br />
          <CodeBlock>npm i @inmoment/the-kitchen-angular</CodeBlock>
          <br />
          <p className="kxi-text-large">
            In your <code>app.module.ts</code>{' '}
          </p>
          <br />
          <CodeBlock
            code={`import { KitchenAngularModule } from '@inmoment/the-kitchen-angular';
// ...
imports: [
  // ...
  KitchenAngularModule
  // ...
],
// ...
`}
          />
        </p>
      </>
    ),
  },
  {
    title: 'How do I use Slots / Children?',
    content: (
      <>
        <AlertInlineMain
          color="error"
          title="Important!"
          subtitle="While Kitchen components contain slots their implementation is slightly different from Web Component Slots. See below for the basic implementation. React implementation will remain the same (through the usage of 'props.children') "
        />
        <br />
        <CodeBlock
          code={`<xi-basic-tile>
  <span slot="children">Beautifully formatted text here.</span>
</xi-basic-tile>`}
        />
        <br />
        <p className="kxi-text-600 kxi-text-medium">
          While most components will use slot="children", some may use a slot name corresponding to a specific location of the component. Check the
          CodeSnippet in your language to see specific implementation for the component.
        </p>
        <p className="kxi-text-600 kxi-text-medium">
          To determine if a component supports slots look for the `children` property and the `slotName` property in the props. If you have any
          questions please reach out to the #the-kitchen-inquiries slack channel
        </p>
        <br />
        <br />
      </>
    ),
  },
  {
    title: 'Which browsers does the Kitchen support?',
    content: (
      <>
        <section>
          <p>
            The Kitchen supports all XI-supported browsers. Up-to-date information can be found at the &nbsp;
            <ExternalLink url={supportStatementUrl} title="XI Browser Support Statement" />.
          </p>
          <br />
          <p>These include the past year's versions of:</p>
          <ul>
            <li>
              <ExternalLink url="https://www.google.com/chrome" title="Chrome" />
            </li>
            <li>
              <ExternalLink url="https://www.mozilla.org/firefox" title="Firefox" />*
            </li>
            <li>
              <ExternalLink url="https://www.apple.com/safari" title="Safari" />
              **
            </li>
            <li>
              Any browser that uses the <ExternalLink url="https://webkit.org/" title="WebKit" /> or
              <ExternalLink url="https://www.chromium.org/Home/" title="Chromium" /> engines.
              <ul>
                <li>
                  <ExternalLink url="https://en.wikipedia.org/wiki/Chromium_(web_browser)#Active" title="Chromium based browsers" />
                </li>
                <li>
                  <ExternalLink url="https://en.wikipedia.org/wiki/List_of_web_browsers#WebKit-based" title="WebKit based browsers" />
                </li>
              </ul>
            </li>
          </ul>
        </section>
        <br />

        <section>
          *Firefox
          <ul>
            <li>
              *On macOS, Firefox may skip links in tab navigation. This is due to an OS level setting. See the following pages for more information:
            </li>
            <ul>
              {Object.entries(appleMacOsSupportUrls).map(([title, url]) => (
                <li key={url}>
                  <ExternalLink url={url} title={title} />
                </li>
              ))}
            </ul>
          </ul>
        </section>
        <section>
          **Safari:
          <ul>
            <li>
              **By default, Safari will <strong>only</strong> navigate to text boxes and lists with the Tab key. Use Option-Tab to tab to all
              focusable elements. See <ExternalLink url={appleSafariSupportUrl} title="Keyboard shortcuts and gestures in Safari on Mac" /> for more
              information.
            </li>
          </ul>
        </section>
        <br />
      </>
    ),
  },
  {
    title: 'What is the history behind The Kitchen?',
    content: (
      <>
        <h3>Mission Statement/History: The Kitchen Design Team</h3>
        <br />
        <p>
          In the genesis of XI's evolution, visionary prototypes began to shape the future. Fueled by the ambition to transform these prototypes into
          tangible, remarkable products, our CEO, Andrew Joiner, founded the Garage. Embracing the spirit that "All Amazing Products Start in a
          Garage," the Garage burgeoned, prompting the need for a cohesive system of rules and standards. This gave rise to the Kitchen Design System.
        </p>
        <br />
        <p>
          As the Kitchen emerged, it bore the mantle of simplicity and efficiency, mirroring the beauty cultivated in the Garage's designs. Andrew's
          vision transcended into a dedicated team—The Kitchen, with a mission to streamline and extend the elegance of the Garage's creations.
        </p>
        <br />
        <p>
          In essence, The Kitchen is the custodian of XI's individuality, ensuring a custom feel while fostering consistency across global teams. Our
          journey is anchored in the seamless fusion of creativity and structure, exemplified by the Kitchen Design System.
        </p>
        <br />
        <h3>Our Goals:</h3>
        <ol>
          <li>
            <strong>Reducing Development Time:</strong> Striving for efficiency without compromising quality, we aim to accelerate development cycles.
          </li>

          <li>
            <strong>Simplifying the Codebase:</strong> Dedication to simplicity ensures a codebase that is not only powerful but also comprehensible.
          </li>

          <li>
            <strong>Standardizing User Interfaces:</strong> Establishing uniformity in user interfaces promotes a seamless and intuitive user
            experience.
          </li>

          <li>
            <strong>Providing Baked-in ADA Compliance:</strong> Inclusivity is paramount; our designs are crafted with accessibility in mind, ensuring
            ADA compliance is integral.
          </li>

          <li>
            <strong>Reducing Complexity:</strong> We actively pursue simplicity, minimizing unnecessary intricacies in our designs and
            implementations.
          </li>

          <li>
            <strong>Allowing Rapid Iteration:</strong> Facilitating agile development, we foster an environment conducive to rapid and iterative
            improvements.
          </li>

          <li>
            <strong>Improving Design Consistency:</strong> Striving for a harmonious visual language, we uphold design consistency across the XI
            landscape.
          </li>
        </ol>

        <h3>Breaking Changes Policy:</h3>
        <br />

        <p>
          The Kitchen Design Team is committed to stability and compatibility. Our breaking changes policy, detailed{' '}
          <a href="https://inmoment.atlassian.net/wiki/spaces/SoftwareDEV/pages/19717580/Breaking+Changes+Policy">here</a>, succinctly states:
        </p>
        <br />

        <p>
          <em>
            The Kitchen will not knowingly release a minor or patch version that contains a breaking change. In the event that bugs in minor or patch
            versions inadvertently cause a breaking change, The Kitchen will promptly revert or update the change to ensure existing code continues to
            function. This policy extends to deprecated code, ensuring continued support through the end of the current major version.
          </em>
        </p>
        <br />

        <p>
          <em>
            Major version releases of the Kitchen may include breaking changes, necessitating refactoring of existing code. We prioritize transparent
            communication and support to ease the transition for our users.
          </em>
        </p>
      </>
    ),
  },
];
